import React, { useState } from 'react';

import type { UseFloatingOptions } from '@floating-ui/react';
import {
  useClick, useDismiss, useInteractions, useRole,
  autoUpdate, offset, shift, useFloating,
} from '@floating-ui/react';

export interface RequiredAnchorProps {
  onClose?: VoidFunction | undefined;
  onClick: VoidFunction;
}

export interface RequiredPopoverProps {
  onClose: VoidFunction;
  style?: React.CSSProperties;
}

type RequiredAnchorPropsWithRef = RequiredAnchorProps & React.RefAttributes<unknown>;
type RequiredPopoverPropsWithRef = RequiredPopoverProps & React.RefAttributes<HTMLDivElement>;

export interface PossiblePopoverProps {
  canClose?: boolean | undefined;
}

const isCalendarOpen = () => document.querySelector('.flatpickr-calendar.open');

// Вложенный поповер (например, окно score внутри «Типы событий») рендерится
// порталом в body, поэтому для внешнего поповера он «снаружи». Не закрываем
// родителя, если нажатие пришлось на вложенный поповер.
const isNestedPopover = (target: EventTarget | null): boolean => (
  target instanceof Element && !!target.closest('[data-floating-popover]')
);

//  Использование:
// const Component = withPopover(UseFloatingOptions)(PopoverComponent)(AnchorComponent)
const withPopover = (options: UseFloatingOptions) => <
  TAnchorProps extends RequiredAnchorPropsWithRef = RequiredAnchorPropsWithRef,
>(AnchorComponent: React.ForwardRefExoticComponent<TAnchorProps>) => <
  TPopoverProps extends RequiredPopoverPropsWithRef = RequiredPopoverPropsWithRef,

>(PopoverComponent: React.ForwardRefExoticComponent<TPopoverProps>) => function (compoundAnchorProps: Omit<TAnchorProps, keyof RequiredAnchorProps> & {
  popoverProps?: (Partial<Omit<TPopoverProps & PossiblePopoverProps, keyof RequiredPopoverProps>>) | undefined;
}): React.JSX.Element {
      const { popoverProps, ...anchorProps } = compoundAnchorProps;
      const [isOpen, setIsOpen] = useState(false);

      const { refs, floatingStyles, context } = useFloating({
        middleware: [offset(12), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
        strategy: 'fixed',
        ...options,
        open: isOpen,
        onOpenChange: setIsOpen,
      });

      const click = useClick(context, { enabled: false });
      const dismiss = useDismiss(context, {
        outsidePress: (event) => (
          !isCalendarOpen()
          && !isNestedPopover(event.target)
          && (popoverProps?.canClose ?? true)
        ),
        escapeKey: true,
      });
      const role = useRole(context);
      // @ts-expect-error хук не доработан
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { getReferenceProps, getFloatingProps } = useInteractions([
        click,
        dismiss,
        role,
      ]);
      const openPopover = () => setIsOpen(true);
      const closePopover = () => {
        if (popoverProps?.canClose ?? true) setIsOpen((prev) => !prev);
      };

      return (
        <React.Fragment>
          <AnchorComponent
            {...anchorProps as TAnchorProps}
            onClick={openPopover}
            ref={refs.setReference}
          />
          <PopoverComponent
            {...popoverProps as TPopoverProps}
            onClose={closePopover}
            ref={refs.setFloating}
            style={{ ...floatingStyles, display: isOpen ? 'block' : 'none' }}
          />
        </React.Fragment>
      );
    };

export { withPopover };
